import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  Calendar, Bell, Phone, Mic, Eye, MessageCircle, 
  Clock, Shield, Star, Users, Heart, Zap,
  Instagram, Facebook, Youtube, MessageSquare,
  ArrowRight, CheckCircle, Globe, Smartphone
} from 'lucide-react';

// TikTok icon component
const TikTokIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

const Marketing = () => {
  const { language } = useLanguage();

  const content = {
    fr: {
      hero: {
        badge: "Plateforme N°1 au Luxembourg",
        title: "Prenez rendez-vous médical en quelques secondes",
        subtitle: "drbooking révolutionne la prise de rendez-vous médicaux avec l'intelligence artificielle",
        cta: "Commencer gratuitement"
      },
      features: [
        {
          icon: Bell,
          title: "Alertes créneaux libérés",
          description: "Soyez averti EN PRIORITÉ dès qu'un rendez-vous se libère chez votre médecin. Plus besoin d'attendre des semaines !",
          highlight: true
        },
        {
          icon: Phone,
          title: "Assistant vocal intelligent",
          description: "Prenez rendez-vous par la voix grâce à notre assistant IA. Idéal pour les personnes malvoyantes ou en situation de handicap.",
          highlight: true
        },
        {
          icon: Mic,
          title: "Répondeur vocal pour professionnels",
          description: "Les médecins peuvent cloner leur voix pour répondre automatiquement aux patients. Gain de temps énorme !",
          highlight: true
        },
        {
          icon: MessageCircle,
          title: "Réservation discrète par message",
          description: "Prenez rendez-vous discrètement depuis votre lieu de travail via WhatsApp ou SMS. Confidentialité garantie.",
          highlight: true
        },
        {
          icon: Clock,
          title: "Disponible 24h/24, 7j/7",
          description: "Réservez à tout moment, même la nuit ou le week-end. Fini les appels qui ne répondent pas !"
        },
        {
          icon: Shield,
          title: "100% RGPD compliant",
          description: "Vos données de santé sont protégées selon les normes européennes les plus strictes."
        },
        {
          icon: Globe,
          title: "Multilingue",
          description: "Interface disponible en français, anglais, allemand, luxembourgeois, et plus encore."
        },
        {
          icon: Smartphone,
          title: "Mobile first",
          description: "Application optimisée pour tous les écrans : smartphone, tablette, ordinateur."
        }
      ],
      socialTitle: "Suivez-nous sur les réseaux sociaux",
      socialSubtitle: "Restez informé des dernières actualités et fonctionnalités",
      testimonialTitle: "Ils nous font confiance",
      ctaTitle: "Prêt à révolutionner vos rendez-vous médicaux ?",
      ctaSubtitle: "Rejoignez des milliers de patients et professionnels au Luxembourg"
    },
    en: {
      hero: {
        badge: "#1 Platform in Luxembourg",
        title: "Book your medical appointment in seconds",
        subtitle: "drbooking revolutionizes medical appointments with artificial intelligence",
        cta: "Start for free"
      },
      features: [
        {
          icon: Bell,
          title: "Slot release alerts",
          description: "Be notified FIRST when an appointment becomes available with your doctor. No more waiting weeks!",
          highlight: true
        },
        {
          icon: Phone,
          title: "Intelligent voice assistant",
          description: "Book appointments by voice with our AI assistant. Ideal for visually impaired or disabled people.",
          highlight: true
        },
        {
          icon: Mic,
          title: "Voice answering for professionals",
          description: "Doctors can clone their voice to automatically respond to patients. Huge time savings!",
          highlight: true
        },
        {
          icon: MessageCircle,
          title: "Discreet booking via message",
          description: "Book appointments discreetly from work via WhatsApp or SMS. Privacy guaranteed.",
          highlight: true
        },
        {
          icon: Clock,
          title: "Available 24/7",
          description: "Book anytime, even at night or on weekends. No more unanswered calls!"
        },
        {
          icon: Shield,
          title: "100% GDPR compliant",
          description: "Your health data is protected according to the strictest European standards."
        },
        {
          icon: Globe,
          title: "Multilingual",
          description: "Interface available in French, English, German, Luxembourgish, and more."
        },
        {
          icon: Smartphone,
          title: "Mobile first",
          description: "Application optimized for all screens: smartphone, tablet, computer."
        }
      ],
      socialTitle: "Follow us on social media",
      socialSubtitle: "Stay informed about the latest news and features",
      testimonialTitle: "They trust us",
      ctaTitle: "Ready to revolutionize your medical appointments?",
      ctaSubtitle: "Join thousands of patients and professionals in Luxembourg"
    },
    de: {
      hero: {
        badge: "Plattform Nr. 1 in Luxemburg",
        title: "Buchen Sie Ihren Arzttermin in Sekunden",
        subtitle: "drbooking revolutioniert Arzttermine mit künstlicher Intelligenz",
        cta: "Kostenlos starten"
      },
      features: [
        {
          icon: Bell,
          title: "Benachrichtigungen bei freien Terminen",
          description: "Werden Sie ZUERST benachrichtigt, wenn ein Termin bei Ihrem Arzt frei wird. Kein wochenlanges Warten mehr!",
          highlight: true
        },
        {
          icon: Phone,
          title: "Intelligenter Sprachassistent",
          description: "Buchen Sie Termine per Sprache mit unserem KI-Assistenten. Ideal für sehbehinderte Menschen.",
          highlight: true
        },
        {
          icon: Mic,
          title: "Sprachantwort für Ärzte",
          description: "Ärzte können ihre Stimme klonen, um automatisch auf Patienten zu antworten. Enorme Zeitersparnis!",
          highlight: true
        },
        {
          icon: MessageCircle,
          title: "Diskrete Buchung per Nachricht",
          description: "Buchen Sie diskret von der Arbeit über WhatsApp oder SMS. Datenschutz garantiert.",
          highlight: true
        },
        {
          icon: Clock,
          title: "Rund um die Uhr verfügbar",
          description: "Buchen Sie jederzeit, auch nachts oder am Wochenende."
        },
        {
          icon: Shield,
          title: "100% DSGVO-konform",
          description: "Ihre Gesundheitsdaten sind nach strengsten europäischen Standards geschützt."
        },
        {
          icon: Globe,
          title: "Mehrsprachig",
          description: "Oberfläche auf Französisch, Englisch, Deutsch, Luxemburgisch und mehr."
        },
        {
          icon: Smartphone,
          title: "Mobile first",
          description: "Für alle Bildschirme optimiert: Smartphone, Tablet, Computer."
        }
      ],
      socialTitle: "Folgen Sie uns in den sozialen Medien",
      socialSubtitle: "Bleiben Sie über die neuesten Nachrichten informiert",
      testimonialTitle: "Sie vertrauen uns",
      ctaTitle: "Bereit, Ihre Arzttermine zu revolutionieren?",
      ctaSubtitle: "Schließen Sie sich Tausenden von Patienten und Fachleuten in Luxemburg an"
    },
    es: {
      hero: {
        badge: "Plataforma N°1 en Luxemburgo",
        title: "Reserve su cita médica en segundos",
        subtitle: "drbooking revoluciona las citas médicas con inteligencia artificial",
        cta: "Comenzar gratis"
      },
      features: [
        {
          icon: Bell,
          title: "Alertas de citas disponibles",
          description: "Sea notificado PRIMERO cuando una cita esté disponible con su médico. ¡No más esperas de semanas!",
          highlight: true
        },
        {
          icon: Phone,
          title: "Asistente de voz inteligente",
          description: "Reserve citas por voz con nuestro asistente IA. Ideal para personas con discapacidad visual.",
          highlight: true
        },
        {
          icon: Mic,
          title: "Contestador de voz para profesionales",
          description: "Los médicos pueden clonar su voz para responder automáticamente. ¡Ahorro de tiempo enorme!",
          highlight: true
        },
        {
          icon: MessageCircle,
          title: "Reserva discreta por mensaje",
          description: "Reserve citas discretamente desde el trabajo por WhatsApp o SMS. Privacidad garantizada.",
          highlight: true
        },
        {
          icon: Clock,
          title: "Disponible 24/7",
          description: "Reserve en cualquier momento, incluso de noche o los fines de semana."
        },
        {
          icon: Shield,
          title: "100% compatible con RGPD",
          description: "Sus datos de salud están protegidos según las normas europeas más estrictas."
        },
        {
          icon: Globe,
          title: "Multilingüe",
          description: "Interfaz disponible en francés, inglés, alemán, luxemburgués y más."
        },
        {
          icon: Smartphone,
          title: "Mobile first",
          description: "Aplicación optimizada para todas las pantallas: móvil, tablet, ordenador."
        }
      ],
      socialTitle: "Síguenos en redes sociales",
      socialSubtitle: "Mantente informado de las últimas novedades",
      testimonialTitle: "Confían en nosotros",
      ctaTitle: "¿Listo para revolucionar sus citas médicas?",
      ctaSubtitle: "Únase a miles de pacientes y profesionales en Luxemburgo"
    }
  };

  const t = content[language] || content.fr;

  const socialLinks = [
    { name: 'Instagram', icon: Instagram, url: 'https://instagram.com/drbooking.pro', color: 'hover:bg-gradient-to-r hover:from-purple-500 hover:to-pink-500' },
    { name: 'Facebook', icon: Facebook, url: 'https://facebook.com/drbooking.pro', color: 'hover:bg-blue-600' },
    { name: 'TikTok', icon: TikTokIcon, url: 'https://tiktok.com/@drbooking.pro', color: 'hover:bg-black' },
    { name: 'YouTube', icon: Youtube, url: 'https://youtube.com/@drbooking.pro', color: 'hover:bg-red-600' },
    { name: 'WhatsApp', icon: MessageSquare, url: 'https://wa.me/352621411475', color: 'hover:bg-green-500' },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Hero Section */}
      <section className="relative overflow-hidden py-20 px-4">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-600/10 via-transparent to-cyan-600/10"></div>
        <div className="max-w-6xl mx-auto relative z-10">
          <div className="text-center">
            <Badge className="mb-6 bg-blue-100 text-blue-700 px-4 py-2 text-sm">
              <Star className="w-4 h-4 mr-2 inline" />
              {t.hero.badge}
            </Badge>
            <h1 className="text-4xl md:text-6xl font-bold text-slate-900 mb-6 leading-tight">
              {t.hero.title}
            </h1>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto mb-10">
              {t.hero.subtitle}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/register">
                <Button size="lg" className="bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-700 hover:to-cyan-600 text-lg px-8 py-6">
                  {t.hero.cta}
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to="/professionals">
                <Button size="lg" variant="outline" className="text-lg px-8 py-6">
                  <Users className="mr-2 w-5 h-5" />
                  Voir les professionnels
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Pourquoi choisir drbooking ?
            </h2>
            <p className="text-lg text-slate-600">
              Des fonctionnalités uniques pour patients et professionnels
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {t.features.map((feature, index) => (
              <Card 
                key={index} 
                className={`relative overflow-hidden transition-all duration-300 hover:shadow-xl hover:-translate-y-1 ${
                  feature.highlight 
                    ? 'border-2 border-blue-500 bg-gradient-to-br from-blue-50 to-cyan-50' 
                    : 'border border-slate-200'
                }`}
              >
                {feature.highlight && (
                  <div className="absolute top-0 right-0 bg-gradient-to-r from-blue-500 to-cyan-500 text-white text-xs px-3 py-1 rounded-bl-lg font-medium">
                    EXCLUSIF
                  </div>
                )}
                <CardContent className="p-6">
                  <div className={`w-14 h-14 rounded-xl flex items-center justify-center mb-4 ${
                    feature.highlight 
                      ? 'bg-gradient-to-r from-blue-500 to-cyan-500' 
                      : 'bg-slate-100'
                  }`}>
                    <feature.icon className={`w-7 h-7 ${feature.highlight ? 'text-white' : 'text-slate-600'}`} />
                  </div>
                  <h3 className="text-lg font-bold text-slate-900 mb-2">{feature.title}</h3>
                  <p className="text-slate-600 text-sm leading-relaxed">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Accessibility Section */}
      <section className="py-16 px-4 bg-gradient-to-r from-purple-600 to-blue-600">
        <div className="max-w-4xl mx-auto text-center text-white">
          <Eye className="w-16 h-16 mx-auto mb-6 opacity-90" />
          <h2 className="text-3xl font-bold mb-4">Accessible à tous</h2>
          <p className="text-xl opacity-90 mb-8">
            Notre assistant vocal permet aux personnes malvoyantes ou en situation de handicap 
            de prendre rendez-vous facilement, sans avoir à naviguer sur un écran.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Badge className="bg-white/20 text-white px-4 py-2">
              <CheckCircle className="w-4 h-4 mr-2 inline" />
              Commande vocale
            </Badge>
            <Badge className="bg-white/20 text-white px-4 py-2">
              <CheckCircle className="w-4 h-4 mr-2 inline" />
              Compatible lecteurs d'écran
            </Badge>
            <Badge className="bg-white/20 text-white px-4 py-2">
              <CheckCircle className="w-4 h-4 mr-2 inline" />
              Navigation simplifiée
            </Badge>
          </div>
        </div>
      </section>

      {/* Social Media Section */}
      <section className="py-20 px-4 bg-slate-50">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-slate-900 mb-4">{t.socialTitle}</h2>
          <p className="text-lg text-slate-600 mb-10">{t.socialSubtitle}</p>
          
          <div className="flex flex-wrap justify-center gap-4">
            {socialLinks.map((social) => (
              <a
                key={social.name}
                href={social.url}
                target="_blank"
                rel="noopener noreferrer"
                className={`flex items-center gap-3 bg-white border border-slate-200 rounded-full px-6 py-3 transition-all duration-300 hover:text-white hover:border-transparent hover:shadow-lg ${social.color}`}
              >
                <social.icon className="w-5 h-5" />
                <span className="font-medium">{social.name}</span>
              </a>
            ))}
          </div>

          <div className="mt-10 p-6 bg-green-50 border border-green-200 rounded-2xl inline-block">
            <p className="text-green-800 font-medium">
              <MessageSquare className="w-5 h-5 inline mr-2" />
              Contactez-nous directement sur WhatsApp : <strong>+352 621 411 475</strong>
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto">
          <Card className="bg-gradient-to-r from-blue-600 to-cyan-500 border-0 overflow-hidden relative">
            <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZGVmcz48cGF0dGVybiBpZD0iYSIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSIgd2lkdGg9IjQwIiBoZWlnaHQ9IjQwIiBwYXR0ZXJuVHJhbnNmb3JtPSJyb3RhdGUoNDUpIj48cGF0aCBkPSJNLTEwIDMwaDYwdi0xaC02MHoiIGZpbGw9InJnYmEoMjU1LDI1NSwyNTUsMC4xKSIvPjwvcGF0dGVybj48L2RlZnM+PHJlY3QgZmlsbD0idXJsKCNhKSIgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIvPjwvc3ZnPg==')] opacity-30"></div>
            <CardContent className="p-12 text-center relative z-10">
              <Heart className="w-16 h-16 text-white/80 mx-auto mb-6" />
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                {t.ctaTitle}
              </h2>
              <p className="text-xl text-white/90 mb-8">
                {t.ctaSubtitle}
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/register">
                  <Button size="lg" className="bg-white text-blue-600 hover:bg-slate-100 text-lg px-8 py-6">
                    S'inscrire comme patient
                  </Button>
                </Link>
                <Link to="/register?type=professional">
                  <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 text-lg px-8 py-6">
                    S'inscrire comme professionnel
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-12 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
                <Calendar className="w-6 h-6 text-white" />
              </div>
              <span className="font-bold text-xl">drbooking</span>
            </div>
            
            <div className="flex gap-4">
              {socialLinks.map((social) => (
                <a
                  key={social.name}
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-slate-800 rounded-full flex items-center justify-center hover:bg-blue-600 transition-colors"
                >
                  <social.icon className="w-5 h-5" />
                </a>
              ))}
            </div>
          </div>
          
          <div className="border-t border-slate-800 mt-8 pt-8 text-center text-slate-400 text-sm">
            <p>© 2026 drbooking. Tous droits réservés. Luxembourg.</p>
            <p className="mt-2">
              <Link to="/terms" className="hover:text-white">CGU</Link>
              {' • '}
              <Link to="/privacy" className="hover:text-white">Politique de confidentialité</Link>
              {' • '}
              <Link to="/contact" className="hover:text-white">Contact</Link>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Marketing;
